import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator
} from '@/components/ui/Command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/Popover';
import { Separator } from '@/components/ui/Separator';
import { cn } from '@/utils';
import { CheckIcon, ChevronDown, XIcon } from 'lucide-react';
import * as React from 'react';

export const MultiSelect = React.forwardRef(
    (
        {
            options,
            onValueChange,
            defaultValue = [],
            placeholder = i18next.t('ui.select_options'),
            maxCount = 3,
            modalPopover = false,
            className,
            hideSelectAll = false,
            searchable = true,
            emptyIndicator,
            autoSize = false,
            singleLine = false,
            popoverClassName,
            disabled = false,
            responsive,
            minWidth,
            maxWidth,
            deduplicateOptions = false,
            resetOnDefaultValueChange = true,
            closeOnSelect = false,
            ...props
        },
        ref
    ) => {
        const
            [selectedValues, setSelectedValues] = React.useState(defaultValue),
            [isPopoverOpen, setIsPopoverOpen] = React.useState(false),
            [searchValue, setSearchValue] = React.useState('');

        const
            multiSelectId = React.useId(),
            listboxId = `${multiSelectId}-listbox`,
            buttonRef = React.useRef(null);

        React.useEffect(() => {
            if (resetOnDefaultValueChange) {
                setSelectedValues(defaultValue);
            }
        }, [defaultValue, resetOnDefaultValueChange]);

        /* -------------------------------------------------- */
        /* Helpers */
        /* -------------------------------------------------- */
        const isGroupedOptions = opts => opts.length > 0 && 'heading' in opts[0];

        const getAllOptions = React.useCallback(() => {
            if (!options.length) {
                return [];
            }

            const all = isGroupedOptions(options)
                ? options.flatMap(g => g.options)
                : options;

            if (!deduplicateOptions) {
                return all;
            }

            const seen = new Set();

            return all.filter(o => {
                if (seen.has(o.value)) {
                    return false;
                }

                seen.add(o.value);

                return true;
            });
        }, [options, deduplicateOptions]);

        const getOptionByValue = value => getAllOptions().find(o => o.value === value);

        const removeOption = value => {
            if (disabled) {
                return;
            }

            const newValues = selectedValues.filter(v => v !== value);

            setSelectedValues(newValues);
            onValueChange(newValues);
        };

        const toggleOption = value => {
            if (disabled) {
                return;
            }

            const newValues = selectedValues.includes(value)
                ? selectedValues.filter(v => v !== value)
                : [...selectedValues, value];

            setSelectedValues(newValues);
            onValueChange(newValues);

            if (closeOnSelect) {
                setIsPopoverOpen(false);
            }
        };

        const handleClear = () => {
            if (disabled) {
                return;
            }

            setSelectedValues([]);
            onValueChange([]);
        };

        const toggleAll = () => {
            const enabledOptions = getAllOptions().filter(o => !o.disabled);

            if (selectedValues.length === enabledOptions.length) {
                handleClear();
            } else {
                const allValues = enabledOptions.map(o => o.value);

                setSelectedValues(allValues);
                onValueChange(allValues);
            }
        };

        const filteredOptions = React.useMemo(() => {
            if (!searchable || !searchValue) {
                return options;
            }

            const term = searchValue.toLowerCase();

            if (isGroupedOptions(options)) {
                return options.map(group => ({
                    ...group,
                    options: group.options.filter(
                        o =>
                            o.label.toLowerCase().includes(term) ||
                            o.value.toLowerCase().includes(term)
                    )
                })).filter(g => g.options.length);
            }

            return options.filter(
                o => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term)
            );
        }, [options, searchValue, searchable]);

        return (
            <Popover modal={modalPopover} onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
                <PopoverTrigger asChild>
                    <Button
                        ref={buttonRef}
                        {...props}
                        className={cn(
                            'tw-flex tw-min-h-11 tw-h-auto tw-items-center tw-justify-between tw-w-full tw-bg-white hover:tw-bg-white/90 tw-text-foreground tw-border [&_*]:tw-pointer-events-auto',
                            'focus-visible:tw-border-slate-400 focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0',
                            'data-[state=open]:tw-border-slate-400 data-[state=open]:!tw-ring-0 data-[state=open]:!tw-ring-offset-0',
                            className
                        )}
                        disabled={disabled}
                        onClick={() => !disabled && setIsPopoverOpen(p => !p)}
                    >
                        {selectedValues.length > 0 ? (
                            <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
                                <div className="tw-flex tw-flex-wrap tw-gap-1">
                                    {selectedValues.slice(0, maxCount).map(value => {
                                        const option = getOptionByValue(value);
                                        if (!option) {
                                            return null;
                                        }

                                        return (
                                            <Badge
                                                className="tw-bg-primary tw-text-primary-foreground tw-pointer-events-none tw-z-9999"
                                                key={value}
                                            >
                                                {option.label}
                                                <span
                                                    className="tw-mx-1 tw-p-0.5 tw-cursor-pointer tw-pointer-events-auto tw-flex tw-items-center tw-justify-center -tw-mr-1 hover:tw-text-secondary tw-rounded-sm tw-transition-colors"
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        removeOption(value);
                                                    }}
                                                    role="button"
                                                >
                                                    <XIcon className="tw-h-3 tw-w-3"/>
                                                </span>
                                            </Badge>
                                        );
                                    })}
                                    {selectedValues.length > maxCount && (
                                        <Badge variant="secondary">
                                            {i18next.t('ui.plus_more', { count: selectedValues.length - maxCount })}
                                        </Badge>
                                    )}
                                </div>

                                <div className="tw-flex tw-items-center">
                                    <span
                                        className="tw-mr-2 tw-p-0.5 tw-cursor-pointer tw-pointer-events-auto tw-text-muted-foreground tw-flex tw-items-center tw-justify-center -tw-mr-1 hover:tw-text-secondary tw-rounded-sm tw-transition-colors"
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleClear();
                                        }}
                                        role="button"
                                    >
                                        <XIcon className="tw-h-4 tw-w-4"/>
                                    </span>
                                    <Separator className="tw-h-4 tw-mx-1" orientation="vertical"/>
                                    <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground"/>
                                </div>
                            </div>
                        ) : (
                            <div className="tw-flex tw-justify-between tw-w-full">
                                <span className="tw-text-muted-foreground">{placeholder}</span>
                                <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground"/>
                            </div>
                        )}
                    </Button>
                </PopoverTrigger>

                <PopoverContent align="start" className={cn('tw-p-0 tw-min-w-[250px]', popoverClassName)} id={listboxId}>
                    <Command>
                        {searchable && (
                            <CommandInput className="tw-mb-2 !tw-py-0 tw-max-h-10 tw-pl-2 tw-border tw-border-solid tw-border-border" onValueChange={setSearchValue} placeholder={`${i18next.t('ui.buttons.search')}...`} value={searchValue}/>
                        )}
                        <CommandList>
                            <CommandEmpty>{emptyIndicator || i18next.t('indicators.no_results').capitalize()}</CommandEmpty>
                            {!hideSelectAll && !searchValue && (
                                <CommandGroup>
                                    <CommandItem className="tw-cursor-pointer" onSelect={toggleAll}>
                                        <div className={'tw-mr-2 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-primary'}>
                                            <CheckIcon className={cn(
                                                'tw-h-4 tw-w-4, tw-text-primary tw-transition-opacity',
                                                selectedValues.length === getAllOptions().filter(o => !o.disabled).length ? 'tw-opacity-100' : 'tw-opacity-0'
                                            )}/>
                                        </div>
                                        {i18next.t('ui.select_all')}
                                    </CommandItem>
                                </CommandGroup>
                            )}
                            <CommandGroup>
                                {(isGroupedOptions(filteredOptions)
                                    ? filteredOptions.flatMap(g => g.options)
                                    : filteredOptions
                                ).map(option => {
                                    const isSelected = selectedValues.includes(option.value);

                                    return (
                                        <CommandItem
                                            className="tw-cursor-pointer hover:tw-bg-primary hover:tw-text-primary"
                                            disabled={option.disabled}
                                            key={option.value}
                                            onSelect={() => toggleOption(option.value)}
                                        >
                                            <div className={'tw-mr-2 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-primary'}>
                                                <CheckIcon className={cn(
                                                    'tw-h-4 tw-w-4',
                                                    isSelected ? 'tw-text-primary tw-opacity-100' : 'tw-text-transparent tw-opacity-0'
                                                )}/>
                                            </div>
                                            {option.label}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                            <CommandSeparator/>
                            <CommandGroup>
                                <div className="tw-flex tw-flex-col tw-gap-1">
                                    {selectedValues.length > 0 && (
                                        <CommandItem className="tw-justify-center tw-text-destructive" onSelect={handleClear}>
                                            {i18next.t('ui.clear_selection')}
                                        </CommandItem>
                                    )}
                                    <CommandItem className="tw-justify-center" onSelect={() => setIsPopoverOpen(false)}>
                                        {i18next.t('ui.buttons.close')}
                                    </CommandItem>
                                </div>
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        );
    }
);

MultiSelect.displayName = 'MultiSelect';
